import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import axios from 'axios';
import { clearRememberedAccessGuid, setRememberedAccessGuid } from '../utils/rememberedAccessGuid';
import { notifyAppLogout } from '../auth/auth0Session';
import { shouldApplyCheckAuthResult } from './authCheckGuard';

// Exported so counselor "edit on behalf" mode (UX 2.8 / #1060) can supply a
// nested provider whose `user` is the jobseeker being edited, while the real
// network session stays the counselor's. Normal code uses `useAuth`.
export const AuthContext = createContext(null);
const SESSION_CHANGE_KEY = 'rsworks:session-change';

export class SessionNotVerifiedError extends Error {
  constructor(message = 'Session could not be verified. Please open your invite link again.') {
    super(message);
    this.name = 'SessionNotVerifiedError';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuthSeqRef = useRef(0);
  const loginEpochRef = useRef(0);
  // Bumped every time a verified auth result is actually applied to state, so
  // a session probe that started before that application knows its result is
  // no longer current.
  const authenticatedCheckRevRef = useRef(0);
  const sessionChannelRef = useRef(null);
  const sessionIdentityRef = useRef(null);
  // Stamp of the last synchronous identity write (apply/clear). The effect
  // below only back-fills the ref when nothing newer was applied between the
  // render that captured this stamp and the commit — a stale commit must
  // never overwrite an identity that was applied synchronously mid-batch.
  const identityWriteStampRef = useRef(0);
  const renderedIdentityStamp = identityWriteStampRef.current;
  useEffect(() => {
    if (identityWriteStampRef.current !== renderedIdentityStamp) return;
    sessionIdentityRef.current = user?.id ? { userId: user.id, userType } : null;
  }, [user, userType, renderedIdentityStamp]);

  const announceSessionChange = useCallback((action, identity = sessionIdentityRef.current) => {
    // A tab with no current user can still invalidate the shared cookie via the
    // logout POST — that signal must reach peers even though there is no
    // identity to report. Login announcements are only meaningful with a
    // verified userId + userType.
    if (action === 'login' && !(identity?.userId && identity?.userType)) return;
    const message = { action, ...(identity || {}), changedAt: Date.now() };
    try {
      if (sessionChannelRef.current) sessionChannelRef.current.postMessage(message);
      else window.localStorage.setItem(SESSION_CHANGE_KEY, JSON.stringify(message));
    } catch {
      return;
    }
  }, []);

  const applyAuthenticatedUser = useCallback((data) => {
    // Write the identity ref synchronously — the checkAuth revision guard and
    // probe reconciliation read it mid-batch, before any render can commit.
    sessionIdentityRef.current = data.user?.id
      ? { userId: data.user.id, userType: data.userType }
      : null;
    identityWriteStampRef.current += 1;
    setUser(data.user);
    setUserType(data.userType);
    setUserRole(data.user?.role || null);
  }, []);

  const clearAuthenticatedUser = useCallback(() => {
    sessionIdentityRef.current = null;
    identityWriteStampRef.current += 1;
    setUser(null);
    setUserType(null);
    setUserRole(null);
  }, []);

  /**
   * @param {{ clearOnFailure?: boolean, setLoadingState?: boolean }} [options]
   * @returns {Promise<{ authenticated: boolean, stale?: boolean, error?: boolean }>}
   */
  const checkAuth = useCallback(async (options = {}) => {
    const { clearOnFailure = true, setLoadingState = true } = options;
    const seq = ++checkAuthSeqRef.current;
    const epochAtStart = loginEpochRef.current;
    const revAtStart = authenticatedCheckRevRef.current;

    if (setLoadingState) {
      setLoading(true);
    }

    try {
      // skipGlobal401: this verification IS the session check — a 401 here
      // means signed out, and must never re-enter the global expiry handler.
      const res = await axios.get('/api/auth/me', { withCredentials: true, skipGlobal401: true });
      const authenticated = Boolean(res.data?.authenticated);

      if (!shouldApplyCheckAuthResult({
        seq,
        latestSeq: checkAuthSeqRef.current,
        epochAtStart,
        currentEpoch: loginEpochRef.current,
        authenticated,
      })) {
        return { authenticated: false, stale: true };
      }

      // A verified result landed while this request was in flight (a newer
      // check, or a probe reconciliation). An unauthenticated answer — or an
      // authenticated one for a different account — from this older request
      // is history, not proof: only the identity that actually verified may
      // still apply, so a login-owned check resolving after reconciliation
      // for that same account still succeeds.
      if (authenticatedCheckRevRef.current !== revAtStart) {
        const current = sessionIdentityRef.current;
        const matchesVerifiedIdentity =
          authenticated &&
          res.data?.user?.id === current?.userId &&
          res.data?.userType === current?.userType;
        if (!matchesVerifiedIdentity) {
          return { authenticated: false, stale: true };
        }
      }

      if (authenticated) {
        authenticatedCheckRevRef.current += 1;
        applyAuthenticatedUser(res.data);
        return { authenticated: true };
      }

      if (clearOnFailure) {
        clearAuthenticatedUser();
      }
      return { authenticated: false };
    } catch (err) {
      if (!shouldApplyCheckAuthResult({
        seq,
        latestSeq: checkAuthSeqRef.current,
        epochAtStart,
        currentEpoch: loginEpochRef.current,
        authenticated: false,
      })) {
        return { authenticated: false, stale: true };
      }

      // Same guard as the success path: once a newer verified identity was
      // applied, this older request's failure is not proof of expiry.
      if (authenticatedCheckRevRef.current !== revAtStart) {
        return { authenticated: false, stale: true };
      }

      // Only a rejection from the server means the session is gone. Network blips,
      // rate limiting, and 5xx responses must leave an existing session in place,
      // otherwise returning to a tab can drop a perfectly valid session.
      const status = err?.response?.status;
      const rejected = status === 401 || status === 403;
      if (clearOnFailure && rejected) {
        clearAuthenticatedUser();
      }
      return { authenticated: false, error: true, transient: !rejected };
    } finally {
      // Clear loading whenever the newest check settles, even if this call opted out
      // of owning the flag — otherwise a concurrent check can strand it at true.
      if (seq === checkAuthSeqRef.current) {
        setLoading(false);
      }
    }
  }, [applyAuthenticatedUser, clearAuthenticatedUser]);

  /**
   * Read-only session probe for the 401 expiry path. Unlike checkAuth it never
   * touches authenticated state, checkAuthSeqRef, or loading — so probing an
   * old request's 401 cannot strand a login-owned verification as stale.
   *
   * The probe captures the login epoch and the applied-verification revision
   * at start; if either moved before the response lands — a login/logout
   * boundary, or a newer authenticated result actually applied — the probe's
   * answer is reported stale. An attempted checkAuth is deliberately NOT a
   * boundary: a check that starts but settles transient or unauthenticated
   * proves nothing about this probe's session.
   *
   * A fresh authenticated probe carries the verified user plus its captures so
   * reconcileVerifiedSession can apply a different account (lost cross-tab
   * signal) only while nothing else has moved.
   */
  const verifyCurrentSession = useCallback(async () => {
    const probeEpoch = loginEpochRef.current;
    const probeVerifiedRev = authenticatedCheckRevRef.current;
    const boundaryCrossed = () =>
      loginEpochRef.current !== probeEpoch ||
      authenticatedCheckRevRef.current !== probeVerifiedRev;
    try {
      const res = await axios.get('/api/auth/me', { withCredentials: true, skipGlobal401: true });
      if (boundaryCrossed()) {
        return { authenticated: false, stale: true };
      }
      if (res.data?.authenticated) {
        return {
          authenticated: true,
          user: res.data.user,
          userType: res.data.userType,
          probeEpoch,
          probeVerifiedRev,
        };
      }
      return { authenticated: false };
    } catch (err) {
      if (boundaryCrossed()) {
        return { authenticated: false, stale: true };
      }
      // Only a server rejection proves the session is gone; network blips and
      // 5xx responses are transient and must not tear anything down.
      const status = err?.response?.status;
      const rejected = status === 401 || status === 403;
      return { authenticated: false, transient: !rejected };
    }
  }, []);

  /**
   * Apply the identity an authenticated probe verified when it differs from
   * the one on screen — e.g. another tab signed in on the shared cookie but
   * the broadcast was lost. Strictly guarded: the probe must carry live
   * epoch/rev captures — what actually applied, not merely what started —
   * and this bumps none of checkAuthSeqRef or loginEpochRef, so a login-owned
   * check in flight is never invalidated. Same identity is a no-op; this is
   * reconciliation of a verified identity, not a login attempt.
   */
  const reconcileVerifiedSession = useCallback((probe) => {
    if (!probe?.authenticated || !probe.user?.id) return false;
    if (
      probe.probeEpoch !== loginEpochRef.current ||
      probe.probeVerifiedRev !== authenticatedCheckRevRef.current
    ) {
      return false;
    }
    const current = sessionIdentityRef.current;
    if (probe.user.id === current?.userId && probe.userType === current?.userType) {
      return false;
    }
    authenticatedCheckRevRef.current += 1;
    applyAuthenticatedUser({ user: probe.user, userType: probe.userType });
    return true;
  }, [applyAuthenticatedUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const clearClientSession = useCallback(() => {
    loginEpochRef.current += 1;
    checkAuthSeqRef.current += 1;
    announceSessionChange('logout');
    notifyAppLogout();
    clearAuthenticatedUser();
    setLoading(false);
  }, [announceSessionChange, clearAuthenticatedUser]);

  useEffect(() => {
    const receive = (message) => {
      if (!message || !['login', 'logout'].includes(message.action)) return;
      const identity = sessionIdentityRef.current;
      if (message.action === 'logout') {
        // A peer signed out on this shared cookie origin — honored regardless of
        // which user type announced it. Clear the private view and stay signed
        // out: the logout POST may have failed while the cookie still
        // validates, so re-checking here could rehydrate the very session being
        // torn down. A later authenticated login signal or fresh navigation
        // establishes a session.
        loginEpochRef.current += 1;
        checkAuthSeqRef.current += 1;
        notifyAppLogout();
        clearAuthenticatedUser();
        setLoading(false);
        return;
      }
      // Login signals apply across user types: another role signing in on the
      // same cookie origin replaces this tab's session. Skip only the exact
      // identity already on screen so a same-user re-login recovers in place.
      if (message.userId && message.userId === identity?.userId && message.userType === identity?.userType) {
        return;
      }
      loginEpochRef.current += 1;
      checkAuthSeqRef.current += 1;
      clearAuthenticatedUser();
      setLoading(true);
      void checkAuth({ setLoadingState: false });
    };
    const onStorage = (event) => {
      if (event.key !== SESSION_CHANGE_KEY || !event.newValue) return;
      try { receive(JSON.parse(event.newValue)); } catch { return; }
    };
    window.addEventListener('storage', onStorage);
    try {
      if (typeof window.BroadcastChannel === 'function') {
        const channel = new window.BroadcastChannel(SESSION_CHANGE_KEY);
        channel.onmessage = (event) => receive(event.data);
        sessionChannelRef.current = channel;
      }
    } catch {
      sessionChannelRef.current = null;
    }
    return () => {
      window.removeEventListener('storage', onStorage);
      sessionChannelRef.current?.close();
      sessionChannelRef.current = null;
    };
  }, [checkAuth, clearAuthenticatedUser]);

  useEffect(() => {
    if (!user) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkAuth({ setLoadingState: false });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, checkAuth]);

  const loginWithGuid = useCallback(async (guid) => {
    loginEpochRef.current += 1;
    const code = String(guid || '').trim();
    const res = await axios.post(`/api/auth/guid/${code}`, {}, { withCredentials: true });
    applyAuthenticatedUser({ user: res.data.user, userType: 'JOBSEEKER' });

    const verified = await checkAuth({ clearOnFailure: false, setLoadingState: false });
    if (!verified.authenticated) {
      clearAuthenticatedUser();
      throw new SessionNotVerifiedError();
    }

    // Soft-remember after a successful invite open so expiry can offer Continue
    // without a separate "remember me" checkbox (RSW-398). Explicit logout clears it.
    if (code) setRememberedAccessGuid(code);
    announceSessionChange('login', { userId: res.data.user?.id, userType: 'JOBSEEKER' });

    return res.data;
  }, [checkAuth, applyAuthenticatedUser, clearAuthenticatedUser, announceSessionChange]);

  const loginWithCredentials = useCallback(async (email, password) => {
    loginEpochRef.current += 1;
    const res = await axios.post('/api/auth/login', { email, password }, { withCredentials: true });
    applyAuthenticatedUser({ user: res.data.user, userType: 'COUNSELOR' });

    const verified = await checkAuth({ clearOnFailure: false, setLoadingState: false });
    if (!verified.authenticated) {
      clearAuthenticatedUser();
      throw new SessionNotVerifiedError('Counselor session could not be verified. Please sign in again.');
    }
    announceSessionChange('login', { userId: res.data.user?.id, userType: 'COUNSELOR' });

    return res.data;
  }, [checkAuth, applyAuthenticatedUser, clearAuthenticatedUser, announceSessionChange]);

  const loginWithAuth0 = useCallback(async (accessToken) => {
    loginEpochRef.current += 1;
    const res = await axios.post(
      '/api/auth/auth0/callback',
      { accessToken },
      { withCredentials: true },
    );
    applyAuthenticatedUser({ user: res.data.user, userType: 'COUNSELOR' });

    const verified = await checkAuth({ clearOnFailure: false, setLoadingState: false });
    if (!verified.authenticated) {
      clearAuthenticatedUser();
      throw new SessionNotVerifiedError('Counselor session could not be verified. Please sign in again.');
    }
    announceSessionChange('login', { userId: res.data.user?.id, userType: 'COUNSELOR' });

    return res.data;
  }, [checkAuth, applyAuthenticatedUser, clearAuthenticatedUser, announceSessionChange]);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout', {}, { withCredentials: true });
    } catch {
      /* session may already be invalid */
    }
    clearRememberedAccessGuid();
    clearClientSession();
  }, [clearClientSession]);

  const requestJobseekerAccessLink = useCallback(async (email) => {
    const res = await axios.post(
      '/api/auth/jobseeker/request-link',
      { email },
      { withCredentials: true },
    );
    return res.data;
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      userType,
      userRole,
      loading,
      loginWithGuid,
      loginWithCredentials,
      loginWithAuth0,
      logout,
      checkAuth,
      verifyCurrentSession,
      reconcileVerifiedSession,
      clearClientSession,
      requestJobseekerAccessLink,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
